import { useQuery } from 'react-query';
import { groupRegionByState, groupActiveRegionByState } from '@belong/common';
import type { State } from '@belong/common';
import { PayoutPlanVariationModel } from 'src/api/models';
import {
  fetchPayoutPlansService,
  fetchSettingsService,
} from 'src/api/platform';
import { fetchRegionsService } from 'src/api/region';

export function useSettings() {
  const { isLoading, data, error } = useQuery('settings', fetchSettingsService);
  return { isSettingsLoading: isLoading, settings: data, settingsError: error };
}

type PayoutRegionId = Exclude<PayoutPlanVariationModel['regionId'], undefined>;
type PayoutName = Exclude<PayoutPlanVariationModel['name'], undefined>;
type PayoutPlan = Exclude<PayoutPlanVariationModel['plan'], undefined>;
type PayoutPlansByType = Record<PayoutPlan, PayoutPlanVariationModel>;
type PayoutPlansNormalizedByIdAndName = Record<
  PayoutRegionId,
  Record<PayoutName, PayoutPlansByType>
>;

/**
 * The plan slots a name gets the first time it is seen, seeded as empty
 * placeholders and filled in below only for the plans the API returns.
 *
 * The cast is that placeholder state: the record is declared as holding real
 * models and, until it is filled, it does not. Dropping the seed instead would
 * be a behaviour change — `gttl-guaranteed-rent` and `month-year-rent-slice`
 * both read `.Monthly.<field>` without guarding `Monthly` itself, so the slot
 * has to be there. See docs/development/typecheck-baseline-followups.md.
 * Refs: ARCH-56
 */
function seedPlanSlots(): PayoutPlansByType {
  return {
    Monthly: {},
    NoGuarantee: {},
    Yearly: {},
  } as PayoutPlansByType;
}

export function usePayoutPlans() {
  const { isLoading, data, error } = useQuery(
    'payout-plans',
    fetchPayoutPlansService,
    {
      select: (
        payoutPlans: PayoutPlanVariationModel[],
      ): PayoutPlansNormalizedByIdAndName => {
        // Group array by region Id -> name -> plan.
        const payoutPlansNormalizedByIdAndName: PayoutPlansNormalizedByIdAndName =
          {};
        payoutPlans.forEach((payoutPlan) => {
          const { regionId, name, plan } = payoutPlan;
          if (!regionId || !name || !plan) return;

          // `??=` rather than the previous `if (!x) x = …`: both levels hold
          // objects, which are never falsy, so the two are the same test.
          const plansByName = (payoutPlansNormalizedByIdAndName[regionId] ??=
            {});
          const plansByType = (plansByName[name] ??= seedPlanSlots());

          plansByType[plan] = payoutPlan;
        });

        return payoutPlansNormalizedByIdAndName;
      },
    },
  );

  return {
    isPayoutPlansLoading: isLoading,
    payoutPlans: data,
    payoutPlansError: error,
  };
}

export function useRegions() {
  const {
    isLoading,
    data = [],
    error,
  } = useQuery('regions', () => fetchRegionsService());
  const dataWithoutOtherRegions =
    data?.filter((region) => region.status !== 'Other') || [];

  const regionsByState = groupRegionByState(dataWithoutOtherRegions) || {};
  const activeRegionsByState =
    groupActiveRegionByState(dataWithoutOtherRegions) || {};
  const states = Object.keys(regionsByState).sort((a, b) =>
    a.localeCompare(b),
  ) as State['code'][];
  // Same cast as `states` above: `Object.keys` widens to `string[]`, and every
  // consumer indexes `US_STATES` / `activeRegionsByState` with these.
  const activeStates = Object.keys(activeRegionsByState).sort((a, b) =>
    a.localeCompare(b),
  ) as State['code'][];

  return {
    isRegionsLoading: isLoading,
    regions: dataWithoutOtherRegions,
    regionsByState,
    activeRegionsByState,
    states,
    activeStates,
    regionsError: error,
  };
}
